/**
 * ★ Fase 82/85 — Slide HTML multilingua (ES · EN) per le mini-landing Indonesia
 * (/f/es-ba · /f/en-ba Villa Flora · /f/es-lo · /f/en-lo Green Paradise Lombok).
 *
 * Le grafiche IT (/funnels/indonesia/<asset>/0N.webp) hanno il testo
 * italiano "cotto" dentro l'immagine: qui ogni slide è ricostruita in HTML
 * con le STESSE foto (ritagli puliti in /funnels/indonesia/<asset>/es/) e
 * gli STESSI contenuti tradotti (dizionario COPY per lingua). Struttura 1:1
 * con le slide IT.
 *
 * Palette:
 *  - Villa Flora   → avorio/sabbia + oliva (come la grafica boho-minimal)
 *  - Green Paradise→ notte/smeraldo + oro (come la grafica resort sunset)
 */
import React from 'react';
import {
  Waves, BedDouble, Leaf, Sun, Eye, Sofa, Bath, Utensils, Palette, Sprout,
  Flower2, ArrowRight, CalendarDays, MapPin, TrendingUp, FileCheck2, Settings,
  Coins, Users, FileSearch, Home, Gem, ChevronRight, ShowerHead, TreePalm,
} from 'lucide-react';

/* ────────────────────────────────────────────────────────────────────
   COPY — Villa Flora (7 slide)
   ──────────────────────────────────────────────────────────────────── */
const VF_COPY = {
  es: {
    heroAlt: 'Villa Flora Bali — piscina privada y jardín tropical',
    s1: {
      title: 'Villa Flora Bali', kicker: 'Privada. Tropical. Esencial.',
      text: 'Una villa íntima con piscina privada, interiores luminosos y un ambiente relajado.',
      chips: ['Piscina privada', 'Habitación luminosa', 'Diseño boho-minimal'],
      tiles: ['Dormitorio con acceso a la piscina', 'Salón abierto al jardín'],
    },
    s2: {
      title1: 'Dentro de ', title2: 'Villa Flora', sub: 'Materiales naturales, paleta neutra, confort sencillo.',
      labels: ['Habitación', 'Salón', 'Baño', 'Kitchenette'],
      footer: 'Cada ambiente está pensado para que te sientas en Bali desde el primer momento.',
    },
    s3: {
      tiles: ['Piscina privada de día', 'Piscina privada de noche', 'Dormitorio abierto al jardín'],
      title: 'Del sol a la noche', sub: 'La villa cambia de atmósfera, no de identidad.',
      points: ['Piscina privada', 'Verde tropical', 'Relax al aire libre'],
      footer: 'Un pequeño refugio para vivir en cada momento del día.',
    },
    s4: {
      heroAlt: 'Villa Flora Bali — vista de la piscina',
      title: 'Por qué gusta al instante', sub: 'Porque un espacio pequeño, si está bien diseñado, vale mucho más.',
      cards: [
        ['Privacidad real', 'Una piscina solo para ti, rodeada de verde tropical, lejos de miradas indiscretas.'],
        ['Interiores luminosos', 'Espacios amplios, luz natural y atmósferas cálidas que te hacen sentir en casa al instante.'],
        ['Continuidad indoor-outdoor', 'El salón se abre a la piscina para una experiencia de vida fluida y relajada.'],
        ['Estilo auténtico', 'Materiales naturales, paleta neutra y detalles artesanales para una elegancia sencilla y atemporal.'],
      ],
      footer: 'Villa Flora une sencillez y percepción premium.',
    },
    s5: {
      heroAlt: 'La suite de Villa Flora',
      title: 'La suite', sub: 'Una habitación que respira luz, calma y acceso directo al verde.',
      chips: ['Paleta suave', 'Madera cálida', 'Vistas al jardín'],
      tiles: ['Dormitorio de la suite', 'Detalle textil de la suite'],
      footer: 'Aquí el despertar empieza con la piscina a pocos pasos.',
    },
    s6: {
      title: 'Los detalles crean atmósfera', sub: 'Baño luminoso, amenities firmadas Villa Flora, materiales naturales.',
      labels: ['Baño', 'Amenities', 'Toque de salón', 'Kitchenette'],
      alts: ['Baño con luz cenital', 'Amenities Villa Flora', 'Toque de salón', 'Kitchenette'],
      footer: 'Son los detalles adecuados los que hacen parecer sencillo lo que no lo es.',
    },
    s7: {
      heroAlt: 'Villa Flora Bali al anochecer',
      title: 'Villa Flora Bali', kicker: 'No solo una estancia. Atmósfera.',
      text: 'Para quien busca un rincón privado, tropical e inmediatamente deseable.',
      chips: ['Piscina privada', 'Ambiente tropical', 'Espacios esenciales'],
      tiles: ['Piscina de día', 'Habitación', 'Piscina de noche'],
      cta: 'Solicita disponibilidad',
    },
  },
  en: {
    heroAlt: 'Villa Flora Bali — private pool and tropical garden',
    s1: {
      title: 'Villa Flora Bali', kicker: 'Private. Tropical. Essential.',
      text: 'An intimate villa with a private pool, bright interiors and a relaxed atmosphere.',
      chips: ['Private pool', 'Bright bedroom', 'Boho-minimal design'],
      tiles: ['Bedroom with pool access', 'Living room open to the garden'],
    },
    s2: {
      title1: 'Inside ', title2: 'Villa Flora', sub: 'Natural materials, neutral palette, simple comfort.',
      labels: ['Bedroom', 'Living', 'Bathroom', 'Kitchenette'],
      footer: 'Every room is designed to make you feel in Bali from the very first moment.',
    },
    s3: {
      tiles: ['Private pool by day', 'Private pool by night', 'Bedroom open to the garden'],
      title: 'From sun to night', sub: 'The villa changes atmosphere, not identity.',
      points: ['Private pool', 'Tropical greenery', 'Outdoor relaxation'],
      footer: 'A small refuge to live every moment of the day.',
    },
    s4: {
      heroAlt: 'Villa Flora Bali — view of the pool',
      title: 'Why it wins you over instantly', sub: 'Because a small space, when well designed, is worth much more.',
      cards: [
        ['Real privacy', 'A pool just for you, surrounded by tropical greenery, away from prying eyes.'],
        ['Bright interiors', 'Wide spaces, natural light and warm atmospheres that make you feel at home instantly.'],
        ['Indoor-outdoor continuity', 'The living room opens onto the pool for a fluid, relaxed living experience.'],
        ['Authentic style', 'Natural materials, neutral palette and handcrafted details for a simple, timeless elegance.'],
      ],
      footer: 'Villa Flora combines simplicity and premium perception.',
    },
    s5: {
      heroAlt: 'The Villa Flora suite',
      title: 'The suite', sub: 'A bedroom that breathes light, calm and direct access to the greenery.',
      chips: ['Soft palette', 'Warm wood', 'Garden views'],
      tiles: ['Suite bedroom', 'Suite textile detail'],
      footer: 'Here waking up begins with the pool a few steps away.',
    },
    s6: {
      title: 'Details create atmosphere', sub: 'Bright bathroom, Villa Flora signature amenities, natural materials.',
      labels: ['Bathroom', 'Amenities', 'Living touch', 'Kitchenette'],
      alts: ['Bathroom with skylight', 'Villa Flora amenities', 'Living touch', 'Kitchenette'],
      footer: 'It is the right details that make what is not simple look simple.',
    },
    s7: {
      heroAlt: 'Villa Flora Bali at dusk',
      title: 'Villa Flora Bali', kicker: 'Not just a stay. Atmosphere.',
      text: 'For those looking for a private, tropical and instantly desirable corner.',
      chips: ['Private pool', 'Tropical atmosphere', 'Essential spaces'],
      tiles: ['Pool by day', 'Bedroom', 'Pool by night'],
      cta: 'Request availability',
    },
  },
};

/* ────────────────────────────────────────────────────────────────────
   COPY — Green Paradise Lombok (6 slide)
   ──────────────────────────────────────────────────────────────────── */
const GP_COPY = {
  es: {
    logoAria: 'Green Paradise Lombok',
    s1: {
      alt: 'Green Paradise Lombok al atardecer',
      line1: 'Villas de lujo', line2: 'en Bumbang Bay', sub: 'Green Paradise Lombok',
      text: ['No vendemos solo una villa.', 'Te ayudamos a construir una presencia patrimonial en Indonesia con criterio, verificación y visión.'],
      pillars: ['Ubicación icónica', 'Diseño resort', 'Valor en el tiempo'],
      footer: 'Punto de partida inteligente: verificación in situ + análisis estratégico',
    },
    s2: {
      alt: 'Villas Green Paradise al anochecer',
      line1: 'Antes de la villa,', line2: 'viene la estructura', sub: 'Una infraestructura patrimonial nace de 3 elementos:',
      pillars: ['Activo', 'Rendimiento', 'Organización fiscal'],
      footer: 'Solo después tiene sentido elegir el activo adecuado.', small: 'La villa es el medio. La estrategia es lo que la hace realmente patrimonial.',
    },
    s3: {
      alt: 'Salón con vistas al resort',
      line1: 'Por qué estas villas', line2: 'son deseables', sub: 'Espacios, panorama y atmósfera resort que elevan la percepción del valor.',
      tiles: ['Suite con vistas', 'Rooftop panorámico', 'Salón premium'],
      pillars: ['Suite con vistas', 'Salón premium', 'Baños escenográficos', 'Rooftop panorámico'],
      footer: 'Un producto que se vende mejor, se vive mejor y se percibe mejor.',
    },
    s4: {
      alt: 'Vista aérea nocturna del resort',
      line1: 'Los 5 errores', line2: 'que queman valor', sub: 'Lo que evitas cuando entras con verificación y estrategia:',
      items: ['Comprar sin verificación', 'Ladrillo sin gestión', 'Fiscalidad improvisada', 'Activo aislado', 'Salida y herencia confusas'],
      footer: 'La villa por sí sola no basta. Hace falta un sistema.',
    },
    s5: {
      alt: 'Terraza al atardecer en Green Paradise',
      line1: 'El recorrido', line2: 'HCF',
      text: ['Primero se verifica.', 'Luego se analiza.', 'Solo después se elige la villa.'],
      steps: ['Verificación in situ', 'Análisis estratégico', 'Elección del activo', 'Infraestructura patrimonial'],
      footer: 'Green Paradise es el punto de partida.', small: 'No una compra casual.',
    },
    s6: {
      alt: 'Suite con vistas a la piscina del resort',
      line1: 'El siguiente paso', line2: 'no es comprar', sub: 'Es verificar in situ y construir la estrategia adecuada.',
      text: 'Reserva tu verificación en Green Paradise Lombok y solicita el análisis estratégico para construir una infraestructura patrimonial con un activo indonesio.',
      cta1: ['Reserva la', 'Verificación in situ'], cta2a: 'Solicita el ', cta2b: ['Análisis', 'Estratégico'],
      chips: ['Villas', 'Territorio', 'Estrategia', 'Patrimonio'],
    },
  },
  en: {
    logoAria: 'Green Paradise Lombok',
    s1: {
      alt: 'Green Paradise Lombok at sunset',
      line1: 'Luxury villas', line2: 'in Bumbang Bay', sub: 'Green Paradise Lombok',
      text: ['We don’t just sell a villa.', 'We help you build a wealth presence in Indonesia with judgement, verification and vision.'],
      pillars: ['Iconic location', 'Resort design', 'Value over time'],
      footer: 'Smart starting point: on-site verification + strategic analysis',
    },
    s2: {
      alt: 'Green Paradise villas at dusk',
      line1: 'Before the villa,', line2: 'comes the structure', sub: 'A wealth infrastructure is born from 3 elements:',
      pillars: ['Asset', 'Yield', 'Tax organisation'],
      footer: 'Only then does it make sense to choose the right asset.', small: 'The villa is the means. The strategy is what makes it truly wealth-building.',
    },
    s3: {
      alt: 'Living room with views over the resort',
      line1: 'Why these villas', line2: 'are desirable', sub: 'Spaces, panorama and resort atmosphere that raise the perception of value.',
      tiles: ['Suite with a view', 'Panoramic rooftop', 'Premium living room'],
      pillars: ['Suite with a view', 'Premium living room', 'Scenic bathrooms', 'Panoramic rooftop'],
      footer: 'A product that sells better, lives better and is perceived better.',
    },
    s4: {
      alt: 'Aerial night view of the resort',
      line1: 'The 5 mistakes', line2: 'that burn value', sub: 'What you avoid when you enter with verification and strategy:',
      items: ['Buying without verification', 'Bricks without management', 'Improvised taxation', 'Isolated asset', 'Unclear exit and inheritance'],
      footer: 'The villa alone is not enough. You need a system.',
    },
    s5: {
      alt: 'Terrace at sunset in Green Paradise',
      line1: 'The HCF', line2: 'journey',
      text: ['First you verify.', 'Then you analyse.', 'Only then do you choose the villa.'],
      steps: ['On-site verification', 'Strategic analysis', 'Asset selection', 'Wealth infrastructure'],
      footer: 'Green Paradise is the starting point.', small: 'Not a casual purchase.',
    },
    s6: {
      alt: 'Suite with views over the resort pool',
      line1: 'The next step', line2: 'is not buying', sub: 'It is verifying on site and building the right strategy.',
      text: 'Book your verification at Green Paradise Lombok and request the strategic analysis to build a wealth infrastructure with an Indonesian asset.',
      cta1: ['Book the', 'On-site Verification'], cta2a: 'Request the ', cta2b: ['Strategic', 'Analysis'],
      chips: ['Villas', 'Territory', 'Strategy', 'Wealth'],
    },
  },
};

/* ────────────────────────────────────────────────────────────────────
   VILLA FLORA BALI — 7 slide (avorio · oliva · oro)
   ──────────────────────────────────────────────────────────────────── */
const VF = '/funnels/indonesia/villa-flora/es';
const F = {
  bg: '#F5EFE4',
  paper: '#FBF7F0',
  ink: '#3F3A2C',
  muted: '#6B6353',
  gold: '#A98A57',
  goldSoft: '#D8C6A3',
  olive: '#6F7352',
  oliveDeep: '#585C40',
  line: '#E6DCC9',
};

const FLORA_CSS = `
  .vf-slide { position: relative; overflow: hidden; background: ${F.bg}; color: ${F.ink}; }
  .vf-leaf { position: absolute; width: 42vw; max-width: 520px; aspect-ratio: 1; border-radius: 50%; pointer-events: none;
    background: radial-gradient(circle at 50% 50%, rgba(96,118,74,0.16), rgba(96,118,74,0) 68%); filter: blur(6px); }
  .vf-fade { position: absolute; left: 0; right: 0; bottom: 0; height: 46%; pointer-events: none;
    background: linear-gradient(to bottom, rgba(245,239,228,0) 0%, rgba(245,239,228,0.85) 62%, ${F.bg} 100%); }
  .vf-tile { position: relative; overflow: hidden; border-radius: 1.1rem; background: ${F.line}; box-shadow: 0 10px 30px rgba(63,58,44,0.10); }
  .vf-tile img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .5s; }
  .vf-tile:hover img { transform: scale(1.03); }
  .vf-chip { display: inline-flex; align-items: center; gap: .55rem; border-radius: 9999px; border: 1px solid ${F.goldSoft};
    background: rgba(251,247,240,0.92); color: ${F.ink}; padding: .45rem 1rem .45rem .5rem; font-size: .9rem; backdrop-filter: blur(6px); }
  .vf-chip-ico { width: 1.9rem; height: 1.9rem; border-radius: 9999px; display: grid; place-items: center; background: ${F.gold}; color: #FFFBF3; flex: none; }
  .vf-label { position: absolute; left: .9rem; bottom: .9rem; display: inline-flex; align-items: center; gap: .5rem; border-radius: 9999px;
    background: rgba(251,247,240,0.94); color: ${F.ink}; padding: .4rem .9rem .4rem .7rem; font-size: .85rem; box-shadow: 0 6px 18px rgba(0,0,0,0.12); }
  .vf-card { background: ${F.paper}; border: 1px solid ${F.line}; border-radius: 1.25rem; box-shadow: 0 8px 26px rgba(63,58,44,0.08); }
  .vf-badge { width: 3rem; height: 3rem; border-radius: 9999px; display: grid; place-items: center; background: ${F.gold}; color: #FFFBF3; flex: none; }
  .vf-cta { background: ${F.olive}; color: #FFFBF3; border: 1px solid rgba(255,255,255,0.18); box-shadow: 0 14px 34px rgba(88,92,64,0.35);
    transition: transform .2s, box-shadow .2s, background-color .2s; }
  .vf-cta:hover { background: ${F.oliveDeep}; transform: translateY(-1px); box-shadow: 0 18px 40px rgba(88,92,64,0.42); }
  .vf-cta:active { transform: translateY(0); }
  .vf-cta:focus-visible { outline: 2px solid ${F.olive}; outline-offset: 3px; }
  @media (prefers-reduced-motion: reduce) { .vf-tile img, .vf-cta { transition: none; } }
`;

function Ornament() {
  return (
    <div className="flex items-center justify-center gap-3" aria-hidden="true">
      <span className="h-px w-16" style={{ background: F.goldSoft }} />
      <Flower2 className="w-5 h-5" style={{ color: F.gold }} strokeWidth={1.5} />
      <span className="h-px w-16" style={{ background: F.goldSoft }} />
    </div>
  );
}

function Rule() {
  return <span className="block h-px w-24 mx-auto" style={{ background: F.gold }} aria-hidden="true" />;
}

function Chip({ icon: Icon, children }) {
  return (
    <span className="vf-chip font-sans">
      <span className="vf-chip-ico"><Icon className="w-4 h-4" strokeWidth={1.8} /></span>
      {children}
    </span>
  );
}

function Tile({ src, alt, label, icon: Icon, className = '', style }) {
  return (
    <figure className={`vf-tile ${className}`} style={style}>
      <img src={src} alt={alt} loading="lazy" decoding="async" />
      {label ? (
        <figcaption className="vf-label font-sans">
          {Icon ? <Icon className="w-4 h-4" style={{ color: F.gold }} strokeWidth={1.8} /> : null}
          {label}
        </figcaption>
      ) : null}
    </figure>
  );
}

function FloraTitle({ title, kicker, sub, size = 'lg' }) {
  const cls = size === 'lg' ? 'text-5xl sm:text-6xl md:text-7xl' : 'text-4xl sm:text-5xl md:text-6xl';
  return (
    <div className="text-center">
      <h2 className={`font-serif leading-[1.02] ${cls}`} style={{ color: F.ink }}>{title}</h2>
      {kicker ? (
        <p className="font-serif mt-3 text-lg sm:text-xl md:text-2xl tracking-[0.18em]" style={{ color: F.gold }}>{kicker}</p>
      ) : null}
      {sub ? <p className="font-sans mt-3 text-base sm:text-lg" style={{ color: F.muted }}>{sub}</p> : null}
    </div>
  );
}

function FloraFooter({ children }) {
  return (
    <div className="mt-10 text-center">
      <Ornament />
      <p className="font-serif mt-4 text-lg sm:text-xl" style={{ color: F.ink }}>{children}</p>
    </div>
  );
}

function HeroPhoto({ src, alt }) {
  return (
    <div className="relative w-full" style={{ height: 'clamp(300px, 52vw, 680px)' }}>
      <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover" loading="eager" decoding="async" />
      <div className="vf-fade" />
    </div>
  );
}

const VF_CHIP_ICONS = {
  s1: [Waves, BedDouble, Leaf],
  s5: [BedDouble, Leaf, Flower2],
  s7: [Waves, Leaf, Sprout],
};
const VF_LABEL_ICONS_S2 = [BedDouble, Sofa, Bath, Utensils];
const VF_LABEL_ICONS_S6 = [ShowerHead, Flower2, Sofa, Utensils];
const VF_POINT_ICONS = [Waves, Leaf, Sun];
const VF_CARD_ICONS = [Eye, Sun, Leaf, Palette];
const VF_CARD_IMGS = ['tile-privacy', 'tile-light', 'tile-indoor', 'tile-style'];

export function VillaFloraSlides({ lang = 'es', onCta }) {
  const c = VF_COPY[lang] || VF_COPY.es;
  return (
    <div data-testid={`aml-slides-${lang}`} data-lang={lang} className="w-full">
      <style>{FLORA_CSS}</style>

      {/* ── 1 · Hero día ── */}
      <section className="vf-slide" data-testid="aml-slide-1">
        <HeroPhoto src={`${VF}/hero-day.webp`} alt={c.heroAlt} />
        <div className="vf-leaf" style={{ left: '-14vw', top: '30%' }} />
        <div className="vf-leaf" style={{ right: '-12vw', top: '55%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 -mt-24 sm:-mt-32 md:-mt-40 pb-14 md:pb-20">
          <FloraTitle title={c.s1.title} kicker={c.s1.kicker} />
          <div className="mt-5"><Ornament /></div>
          <p className="font-sans text-center mt-5 text-base sm:text-lg max-w-xl mx-auto italic" style={{ color: F.muted }}>{c.s1.text}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {c.s1.chips.map((t, i) => <Chip key={t} icon={VF_CHIP_ICONS.s1[i]}>{t}</Chip>)}
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Tile src={`${VF}/bed-main.webp`} alt={c.s1.tiles[0]} className="sm:col-span-3" style={{ aspectRatio: '16 / 10' }} />
            <Tile src={`${VF}/living-main.webp`} alt={c.s1.tiles[1]} className="sm:col-span-2" style={{ aspectRatio: '5 / 4' }} />
          </div>
        </div>
      </section>

      {/* ── 2 · Dentro / Inside ── */}
      <section className="vf-slide" data-testid="aml-slide-2">
        <div className="vf-leaf" style={{ right: '-16vw', top: '-10%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <FloraTitle size="md" title={<>{c.s2.title1}<span style={{ color: F.oliveDeep }}>{c.s2.title2}</span></>} sub={c.s2.sub} />
          <div className="mt-5"><Rule /></div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['bed-2', 'living-2', 'bath-2', 'kitchen'].map((img, i) => (
              <Tile key={img} src={`${VF}/${img}.webp`} alt={c.s2.labels[i]} label={c.s2.labels[i]} icon={VF_LABEL_ICONS_S2[i]} style={{ aspectRatio: '4 / 3' }} />
            ))}
          </div>
          <FloraFooter>{c.s2.footer}</FloraFooter>
        </div>
      </section>

      {/* ── 3 · Del sol a la noche / From sun to night ── */}
      <section className="vf-slide" data-testid="aml-slide-3">
        <div className="vf-leaf" style={{ left: '-14vw', bottom: '-10%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-6 md:pt-10 pb-14 md:pb-20">
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <Tile src={`${VF}/pool-day.webp`} alt={c.s3.tiles[0]} style={{ aspectRatio: '4 / 5' }} />
            <Tile src={`${VF}/pool-night.webp`} alt={c.s3.tiles[1]} style={{ aspectRatio: '4 / 5' }} />
          </div>
          <div className="mt-10">
            <FloraTitle size="md" title={c.s3.title} sub={c.s3.sub} />
          </div>
          <div className="mt-5"><Rule /></div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <Tile src={`${VF}/bed-garden.webp`} alt={c.s3.tiles[2]} style={{ aspectRatio: '4 / 3', padding: 10, background: F.paper }} />
            <ul className="space-y-5">
              {c.s3.points.map((label, i) => {
                const Icon = VF_POINT_ICONS[i];
                return (
                  <li key={label} className="flex items-center gap-4">
                    <span className="vf-badge" style={{ background: '#9A9A7A' }}><Icon className="w-5 h-5" strokeWidth={1.6} /></span>
                    <span className="font-serif text-2xl" style={{ color: F.ink }}>{label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <FloraFooter>{c.s3.footer}</FloraFooter>
        </div>
      </section>

      {/* ── 4 · Por qué gusta / Why it wins you over ── */}
      <section className="vf-slide" data-testid="aml-slide-4">
        <HeroPhoto src={`${VF}/hero-day.webp`} alt={c.s4.heroAlt} />
        <div className="vf-leaf" style={{ right: '-14vw', top: '35%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 -mt-20 sm:-mt-28 md:-mt-36 pb-14 md:pb-20">
          <FloraTitle size="md" title={c.s4.title} sub={c.s4.sub} />
          <div className="mt-5"><Ornament /></div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.s4.cards.map(([title, text], i) => {
              const Icon = VF_CARD_ICONS[i];
              return (
                <article key={title} className="vf-card p-4 sm:p-5 grid grid-cols-5 gap-4 items-stretch">
                  <div className="col-span-3 flex flex-col">
                    <div className="flex items-center gap-3">
                      <span className="vf-badge"><Icon className="w-5 h-5" strokeWidth={1.6} /></span>
                      <h3 className="font-serif text-xl sm:text-2xl leading-tight" style={{ color: F.ink }}>{title}</h3>
                    </div>
                    <p className="font-sans mt-3 text-sm sm:text-[15px] leading-relaxed" style={{ color: F.muted }}>{text}</p>
                  </div>
                  <div className="col-span-2 vf-tile" style={{ borderRadius: '0.9rem', minHeight: 150 }}>
                    <img src={`${VF}/${VF_CARD_IMGS[i]}.webp`} alt={title} loading="lazy" decoding="async" />
                  </div>
                </article>
              );
            })}
          </div>
          <FloraFooter>{c.s4.footer}</FloraFooter>
        </div>
      </section>

      {/* ── 5 · La suite ── */}
      <section className="vf-slide" data-testid="aml-slide-5">
        <HeroPhoto src={`${VF}/suite.webp`} alt={c.s5.heroAlt} />
        <div className="vf-leaf" style={{ left: '-14vw', top: '40%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 -mt-20 sm:-mt-28 md:-mt-36 pb-14 md:pb-20">
          <FloraTitle title={c.s5.title} sub={c.s5.sub} />
          <div className="mt-5"><Ornament /></div>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {c.s5.chips.map((t, i) => <Chip key={t} icon={VF_CHIP_ICONS.s5[i]}>{t}</Chip>)}
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-5 gap-4">
            <Tile src={`${VF}/bed-3.webp`} alt={c.s5.tiles[0]} className="sm:col-span-3" style={{ aspectRatio: '16 / 9' }} />
            <Tile src={`${VF}/pillow.webp`} alt={c.s5.tiles[1]} className="sm:col-span-2" style={{ aspectRatio: '5 / 4' }} />
          </div>
          <FloraFooter>{c.s5.footer}</FloraFooter>
        </div>
      </section>

      {/* ── 6 · Los detalles / Details ── */}
      <section className="vf-slide" data-testid="aml-slide-6">
        <div className="vf-leaf" style={{ right: '-16vw', top: '-8%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">
          <FloraTitle size="md" title={c.s6.title} sub={c.s6.sub} />
          <div className="mt-5"><Rule /></div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['bath-sky', 'amenities', 'living-touch', 'kitchen'].map((img, i) => (
              <Tile key={img} src={`${VF}/${img}.webp`} alt={c.s6.alts[i]} label={c.s6.labels[i]} icon={VF_LABEL_ICONS_S6[i]} style={{ aspectRatio: '4 / 3' }} />
            ))}
          </div>
          <FloraFooter>{c.s6.footer}</FloraFooter>
        </div>
      </section>

      {/* ── 7 · Hero noche + CTA ── */}
      <section className="vf-slide" data-testid="aml-slide-7">
        <HeroPhoto src={`${VF}/hero-night.webp`} alt={c.s7.heroAlt} />
        <div className="vf-leaf" style={{ right: '-12vw', top: '45%' }} />
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 -mt-24 sm:-mt-32 md:-mt-40 pb-16 md:pb-24">
          <FloraTitle title={c.s7.title} kicker={c.s7.kicker} />
          <div className="mt-5"><Ornament /></div>
          <p className="font-sans text-center mt-5 text-base sm:text-lg max-w-xl mx-auto italic" style={{ color: F.muted }}>{c.s7.text}</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            {c.s7.chips.map((t, i) => <Chip key={t} icon={VF_CHIP_ICONS.s7[i]}>{t}</Chip>)}
          </div>
          <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-4">
            <Tile src={`${VF}/pool-day.webp`} alt={c.s7.tiles[0]} style={{ aspectRatio: '4 / 3' }} />
            <Tile src={`${VF}/bed-2.webp`} alt={c.s7.tiles[1]} style={{ aspectRatio: '4 / 3' }} />
            <Tile src={`${VF}/pool-night.webp`} alt={c.s7.tiles[2]} style={{ aspectRatio: '4 / 3' }} />
          </div>
          <div className="mt-10 flex justify-center">
            <button
              type="button" onClick={onCta}
              className="vf-cta font-serif rounded-full px-8 sm:px-10 py-4 text-xl sm:text-2xl inline-flex items-center gap-3"
              data-testid="aml-slide-cta"
            >
              <CalendarDays className="w-6 h-6" strokeWidth={1.6} /> {c.s7.cta} <ArrowRight className="w-6 h-6" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   GREEN PARADISE LOMBOK — 6 slide (notte · smeraldo · oro)
   ──────────────────────────────────────────────────────────────────── */
const GP = '/funnels/indonesia/green-paradise-lombok/es';
const P = {
  ink: '#0E1511',
  gold: '#D6B25E',
  goldSoft: '#EAD9A6',
  ivory: '#F6F0E2',
  sand: '#CDC3A8',
};

const PARADISE_CSS = `
  .gp-slide { position: relative; overflow: hidden; color: ${P.ivory}; background: ${P.ink}; }
  .gp-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; }
  .gp-shade { position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(to bottom, rgba(14,21,17,0.72) 0%, rgba(14,21,17,0.28) 38%, rgba(14,21,17,0.55) 70%, rgba(14,21,17,0.92) 100%); }
  .gp-card { border-radius: 1.1rem; border: 1px solid rgba(214,178,94,0.55); background: rgba(14,21,17,0.62); backdrop-filter: blur(10px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.35); }
  .gp-ico { width: 3.4rem; height: 3.4rem; border-radius: 9999px; display: grid; place-items: center; border: 1.5px solid ${P.gold}; color: ${P.gold};
    background: rgba(14,21,17,0.55); flex: none; }
  .gp-ico-sm { width: 2.6rem; height: 2.6rem; border-radius: .8rem; display: grid; place-items: center; border: 1px solid ${P.gold}; color: ${P.gold}; flex: none; }
  .gp-ul { display: block; height: 1.5px; width: 3.5rem; background: ${P.gold}; }
  .gp-tile { position: relative; overflow: hidden; border-radius: .9rem; border: 1px solid rgba(214,178,94,0.35); background: #1a2620; }
  .gp-tile img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .gp-btn { border-radius: 1rem; border: 1.5px solid ${P.gold}; background: rgba(14,21,17,0.72); color: ${P.ivory}; text-align: left;
    transition: transform .2s, background-color .2s, box-shadow .2s; box-shadow: 0 14px 36px rgba(0,0,0,0.4); }
  .gp-btn:hover { background: rgba(214,178,94,0.16); transform: translateY(-1px); }
  .gp-btn:active { transform: translateY(0); }
  .gp-btn:focus-visible { outline: 2px solid ${P.gold}; outline-offset: 3px; }
  .gp-grain { position: absolute; inset: 0; opacity: .06; pointer-events: none; mix-blend-mode: overlay;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>"); }
  @media (prefers-reduced-motion: reduce) { .gp-btn { transition: none; } }
`;

function GPLogo({ aria }) {
  return (
    <div className="font-serif leading-none select-none" aria-label={aria} data-testid="gp-logo">
      <Leaf className="w-6 h-6 mx-auto" style={{ color: P.gold }} strokeWidth={1.4} aria-hidden="true" />
      <div className="text-center text-[22px] sm:text-2xl tracking-[0.22em] mt-1" style={{ color: P.ivory }}>GREEN</div>
      <div className="text-center italic text-base sm:text-lg -mt-0.5" style={{ color: P.gold }}>Paradise</div>
      <div className="text-center font-sans text-[9px] sm:text-[10px] tracking-[0.42em] mt-1" style={{ color: P.sand }}>— LOMBOK —</div>
    </div>
  );
}

function GPHeader({ aria }) {
  return (
    <div className="flex items-start justify-between">
      <GPLogo aria={aria} />
      <img src="/funnels/hcf-logo.png" alt="HCF Property" className="h-9 sm:h-10 w-auto" />
    </div>
  );
}

function GPSlide({ bg, alt, testId, children, position, logoAria }) {
  return (
    <section className="gp-slide" data-testid={testId}>
      <img src={bg} alt={alt} className="gp-bg" loading="lazy" decoding="async" style={position ? { objectPosition: position } : undefined} />
      <div className="gp-shade" />
      <div className="gp-grain" />
      <div className="relative max-w-5xl mx-auto px-5 md:px-8 pt-7 md:pt-10 pb-14 md:pb-20 min-h-[88vh] md:min-h-0 flex flex-col">
        <GPHeader aria={logoAria} />
        {children}
      </div>
    </section>
  );
}

function GPTitle({ line1, line2, sub }) {
  return (
    <div className="mt-10 md:mt-14 max-w-2xl">
      <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.02]" style={{ color: P.ivory }}>
        {line1}
        {line2 ? <><br /><span style={{ color: P.gold }}>{line2}</span></> : null}
      </h2>
      {sub ? <p className="font-serif mt-4 text-xl sm:text-2xl md:text-[28px] leading-snug" style={{ color: P.ivory }}>{sub}</p> : null}
      <span className="gp-ul mt-4" aria-hidden="true" />
    </div>
  );
}

function GPFooter({ children, small }) {
  return (
    <div className="mt-10 md:mt-12 text-center">
      <div className="flex items-center justify-center gap-4">
        <span className="hidden sm:block h-px flex-1 max-w-[120px]" style={{ background: `${P.gold}99` }} aria-hidden="true" />
        <p className="font-serif text-xl sm:text-2xl" style={{ color: P.ivory }}>{children}</p>
        <span className="hidden sm:block h-px flex-1 max-w-[120px]" style={{ background: `${P.gold}99` }} aria-hidden="true" />
      </div>
      {small ? <p className="font-serif mt-3 text-base sm:text-lg" style={{ color: P.sand }}>{small}</p> : null}
    </div>
  );
}

function GPPillar({ icon: Icon, children }) {
  return (
    <div className="gp-card px-4 py-6 text-center flex flex-col items-center gap-3">
      <span className="gp-ico"><Icon className="w-6 h-6" strokeWidth={1.5} /></span>
      <span className="font-serif text-xl sm:text-2xl leading-tight" style={{ color: P.ivory }}>{children}</span>
      <span className="gp-ul" aria-hidden="true" />
    </div>
  );
}

const GP_S1_ICONS = [MapPin, TreePalm, TrendingUp];
const GP_S2_ICONS = [Home, TrendingUp, FileCheck2];
const GP_S3_ICONS = [BedDouble, Sofa, ShowerHead, Sun];
const GP_S4_ICONS = [FileSearch, Settings, Coins, MapPin, Users];
const GP_S5_ICONS = [MapPin, FileSearch, Home, TrendingUp];
const GP_S6_ICONS = [Home, MapPin, TrendingUp, Gem];

export function GreenParadiseSlides({ lang = 'es', onCta }) {
  const c = GP_COPY[lang] || GP_COPY.es;
  return (
    <div data-testid={`aml-slides-${lang}`} data-lang={lang} className="w-full">
      <style>{PARADISE_CSS}</style>

      {/* ── 1 · Hero ── */}
      <GPSlide bg={`${GP}/terrace-sunset.webp`} alt={c.s1.alt} testId="aml-slide-1" position="center 60%" logoAria={c.logoAria}>
        <GPTitle line1={c.s1.line1} line2={c.s1.line2} sub={c.s1.sub} />
        <p className="font-serif mt-6 max-w-lg text-lg sm:text-xl leading-relaxed" style={{ color: P.ivory }}>
          {c.s1.text[0]}<br />{c.s1.text[1]}
        </p>
        <div className="mt-auto pt-12 md:pt-16 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {c.s1.pillars.map((t, i) => <GPPillar key={t} icon={GP_S1_ICONS[i]}>{t}</GPPillar>)}
        </div>
        <GPFooter>{c.s1.footer}</GPFooter>
      </GPSlide>

      {/* ── 2 · Struttura ── */}
      <GPSlide bg={`${GP}/villas-facade.webp`} alt={c.s2.alt} testId="aml-slide-2" logoAria={c.logoAria}>
        <GPTitle line1={c.s2.line1} line2={c.s2.line2} sub={c.s2.sub} />
        <div className="mt-auto pt-12 md:pt-16 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {c.s2.pillars.map((t, i) => <GPPillar key={t} icon={GP_S2_ICONS[i]}>{t}</GPPillar>)}
        </div>
        <GPFooter small={c.s2.small}>{c.s2.footer}</GPFooter>
      </GPSlide>

      {/* ── 3 · Desiderabilità ── */}
      <GPSlide bg={`${GP}/living-view.webp`} alt={c.s3.alt} testId="aml-slide-3" logoAria={c.logoAria}>
        <GPTitle line1={c.s3.line1} line2={c.s3.line2} sub={c.s3.sub} />
        <div className="mt-8 grid grid-cols-3 gap-3">
          {['bedroom-view', 'rooftop', 'living-stairs'].map((img, i) => (
            <div key={img} className="gp-tile" style={{ aspectRatio: '4 / 3' }}><img src={`${GP}/${img}.webp`} alt={c.s3.tiles[i]} loading="lazy" decoding="async" /></div>
          ))}
        </div>
        <div className="mt-auto pt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {c.s3.pillars.map((t, i) => <GPPillar key={t} icon={GP_S3_ICONS[i]}>{t}</GPPillar>)}
        </div>
        <GPFooter>{c.s3.footer}</GPFooter>
      </GPSlide>

      {/* ── 4 · 5 errori ── */}
      <GPSlide bg={`${GP}/aerial-pool-night.webp`} alt={c.s4.alt} testId="aml-slide-4" logoAria={c.logoAria}>
        <GPTitle line1={c.s4.line1} line2={c.s4.line2} sub={c.s4.sub} />
        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
          {c.s4.items.map((label, i) => {
            const Icon = GP_S4_ICONS[i];
            return (
              <li key={label} className="gp-card px-4 py-3.5 flex items-center gap-4">
                <span className="gp-ico" style={{ width: '3rem', height: '3rem' }}><Icon className="w-5 h-5" strokeWidth={1.5} /></span>
                <span className="h-8 w-px" style={{ background: `${P.gold}66` }} aria-hidden="true" />
                <div>
                  <span className="font-serif text-xl sm:text-2xl leading-tight block" style={{ color: P.ivory }}>{label}</span>
                  <span className="gp-ul mt-1.5" aria-hidden="true" />
                </div>
              </li>
            );
          })}
        </ul>
        <GPFooter>{c.s4.footer}</GPFooter>
      </GPSlide>

      {/* ── 5 · Il percorso HCF ── */}
      <GPSlide bg={`${GP}/terrace-sunset.webp`} alt={c.s5.alt} testId="aml-slide-5" position="center 35%" logoAria={c.logoAria}>
        <GPTitle line1={c.s5.line1} line2={c.s5.line2} />
        <p className="font-serif mt-6 max-w-lg text-2xl sm:text-3xl leading-snug" style={{ color: P.ivory }}>
          {c.s5.text[0]}<br />{c.s5.text[1]}<br />{c.s5.text[2]}
        </p>
        <div className="mt-auto pt-12 md:pt-16 grid grid-cols-2 md:grid-cols-4 gap-3">
          {c.s5.steps.map((label, i, arr) => {
            const Icon = GP_S5_ICONS[i];
            const n = String(i + 1).padStart(2, '0');
            return (
              <div key={n} className="relative gp-card px-3 py-6 text-center flex flex-col items-center gap-2">
                <span className="gp-ico"><Icon className="w-6 h-6" strokeWidth={1.5} /></span>
                <span className="font-serif text-2xl mt-1" style={{ color: P.gold }}>{n}</span>
                <span className="font-serif text-lg sm:text-xl leading-tight" style={{ color: P.ivory }}>{label}</span>
                <span className="gp-ul" aria-hidden="true" />
                {i < arr.length - 1 ? (
                  <ChevronRight className="hidden md:block absolute -right-3.5 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: P.gold }} aria-hidden="true" />
                ) : null}
              </div>
            );
          })}
        </div>
        <GPFooter small={c.s5.small}>{c.s5.footer}</GPFooter>
      </GPSlide>

      {/* ── 6 · CTA ── */}
      <GPSlide bg={`${GP}/bedroom-view.webp`} alt={c.s6.alt} testId="aml-slide-6" position="center 40%" logoAria={c.logoAria}>
        <GPTitle line1={c.s6.line1} line2={c.s6.line2} sub={c.s6.sub} />
        <p className="font-serif mt-6 max-w-lg text-lg sm:text-xl leading-relaxed" style={{ color: P.ivory }}>{c.s6.text}</p>
        <div className="mt-auto pt-12 md:pt-16 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button type="button" onClick={onCta} className="gp-btn px-5 py-4 flex items-center gap-4" data-testid="aml-slide-cta">
            <span className="gp-ico-sm"><CalendarDays className="w-5 h-5" strokeWidth={1.6} /></span>
            <span className="font-serif text-xl sm:text-2xl leading-tight flex-1">{c.s6.cta1[0]}<br /><span style={{ color: P.gold }}>{c.s6.cta1[1]}</span></span>
            <ChevronRight className="w-5 h-5" style={{ color: P.gold }} />
          </button>
          <button type="button" onClick={onCta} className="gp-btn px-5 py-4 flex items-center gap-4" data-testid="aml-slide-cta-2">
            <span className="gp-ico-sm"><TrendingUp className="w-5 h-5" strokeWidth={1.6} /></span>
            <span className="font-serif text-xl sm:text-2xl leading-tight flex-1">{c.s6.cta2a}<span style={{ color: P.gold }}>{c.s6.cta2b[0]}<br />{c.s6.cta2b[1]}</span></span>
            <ChevronRight className="w-5 h-5" style={{ color: P.gold }} />
          </button>
        </div>
        <div className="mt-8 grid grid-cols-4 gap-2 sm:gap-3">
          {c.s6.chips.map((label, i) => {
            const Icon = GP_S6_ICONS[i];
            return (
              <div key={label} className="text-center flex flex-col items-center gap-2">
                <span className="gp-ico-sm"><Icon className="w-5 h-5" strokeWidth={1.5} /></span>
                <span className="font-serif text-base sm:text-xl" style={{ color: P.ivory }}>{label}</span>
                <span className="gp-ul" style={{ width: '2.5rem' }} aria-hidden="true" />
              </div>
            );
          })}
        </div>
      </GPSlide>
    </div>
  );
}

/* Wrapper per lingua (usati da AssetMiniLanding come `slidesComponent`) */
export const VillaFloraSlidesES = (props) => <VillaFloraSlides lang="es" {...props} />;
export const GreenParadiseSlidesES = (props) => <GreenParadiseSlides lang="es" {...props} />;
export const VillaFloraSlidesEN = (props) => <VillaFloraSlides lang="en" {...props} />;
export const GreenParadiseSlidesEN = (props) => <GreenParadiseSlides lang="en" {...props} />;
